import React from 'react';

import { DeleteOutlined, EditOutlined, RightOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { TargetGroupType, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { PurchaseGroupWithTargetDTO } from '@recurrency/core-api-schema/dist/purchaseGroups/getPurchaseGroups';
import { PurchaseTargetDTO } from '@recurrency/core-api-schema/dist/purchasing/getPurchaseTargets';
import { GetSuppliersQueryParams } from '@recurrency/core-api-schema/dist/suppliers/getSuppliers';

import { usePurchasingStatuses } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/utils';

import { Button } from 'components/Button';
import { AsyncButton } from 'components/Button/AsyncButton';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { Counter } from 'components/Counter';
import { FlexSpace } from 'components/FlexSpace';
import { ConfirmModal } from 'components/Modal/ConfirmModal';
import { EditTargetActionButton } from 'components/recipes/EditTargetActionButton';
import { PriorityDispositionEquationPopover } from 'components/recipes/equation/PriorityDispositionEquationPopover';
import { BadgeStatus, StatusBadge } from 'components/recipes/StatusBadge';
import { Tooltip } from 'components/Tooltip';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { truthy } from 'utils/boolean';
import { formatDate, formatNumber, formatTargetProgressPercent, formatUSD, truncate } from 'utils/formatting';
import { hasUserPermission } from 'utils/roleAndTenant';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { PersistedColumn } from 'utils/tableAndSidePaneSettings/types';
import {
  asKeyOf,
  sortableDateColumn,
  sortableDollarColumn,
  sortableIdColumn,
  sortableNumberColumn,
  sortablePercentColumn,
  sortDirections,
} from 'utils/tables';

import { editButtonOnClick, TargetTypeLabels } from '../utils';
import { SpecialOrderFlag } from './components/SpecialOrderFlag';
import { PurchaseGroupsHashState } from './PurchaseGroupsPage.types';

export function usePurchaseGroupsTableColumns({
  hashState,
  handleTargetValueChange,
  reloadPurchaseGroups,
  onPressEdit,
  onPressOpen,
}: {
  hashState: PurchaseGroupsHashState;
  handleTargetValueChange: () => void;
  reloadPurchaseGroups: () => void;
  onPressEdit: (record: PurchaseGroupWithTargetDTO) => void;
  onPressOpen: (groupId: string) => void;
}) {
  const { shouldShowSpecialOrderWarning } = usePurchasingStatuses();
  const { activeTenant } = useGlobalApp();
  const canCreateGroups = hasUserPermission(activeTenant, TenantUserPermission.ActionCreatePurchaseGroups);
  const canExportOrders = hasUserPermission(activeTenant, TenantUserPermission.ActionExportPurchaseTransferOrders);
  async function handleDelete(groupId: string, groupName: string) {
    const submitNotification = createSubmissionNotification({
      entityName: 'Purchase Group',
      expectedWaitSeconds: 5,
      submittingMessage: 'Deleting Purchase Group',
      fireInProgressNotification: false,
    });

    try {
      const didUserConfirm = await showAsyncModal(ConfirmModal, {
        title: `Delete purchase group ${groupName}?`,
        content: 'Are you sure you want to delete this purchase group? This action cannot be undone.',
        okText: 'Delete',
        okType: 'primary',
        width: 450,
      });

      if (!didUserConfirm) return;

      submitNotification.inProgress();
      await coreApiFetch(schemas.purchaseGroups.deletePurchaseGroup, {
        pathParams: {
          groupId,
        },
      });
      submitNotification.success({ successMessage: 'Purchase Group deleted' });
      reloadPurchaseGroups();
    } catch (err) {
      submitNotification.error(err);
    }
  }

  const { targetGroupType } = hashState;

  const tableColumns: PersistedColumn<PurchaseGroupWithTargetDTO>[] = [
    sortableIdColumn({
      title: 'Group',
      dataIndex: 'groupName',
      sorter: true,
      required: true,
      settingKey: 'groupName',
      render: (_, record: PurchaseGroupWithTargetDTO) => (
        <FlexSpace justify="space-between">
          <ConditionalWrapper
            condition={record.groupName.length >= 18}
            wrapper={(children) => (
              <Tooltip slow title={record.groupName} placement="top">
                <span
                  className={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  `}
                >
                  {children}
                </span>
              </Tooltip>
            )}
          >
            <span
              className={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
              `}
            >
              {truncate(record.groupName, 20)}
            </span>
          </ConditionalWrapper>
          <Tooltip
            overlayStyle={{ whiteSpace: 'pre-line' }}
            slow
            title={`${record.purchaseLocationId}: ${record.purchaseLocationName}
            ${
              record.spokeLocations ? `${record.spokeLocations.map((l) => `${l.foreignId}: ${l.name}`).join('\n')}` : ''
            }`}
            placement="right"
          >
            <Counter
              className={css`
                margin-left: 5px;
              `}
              value={(record.spokeLocations?.length || 0) + 1}
              selected={false}
              variant="primary"
            />
          </Tooltip>
        </FlexSpace>
      ),
    }),
    sortableIdColumn({
      title: 'Supplier',
      dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('supplierId'),
      sorter: true,
      settingKey: 'supplierId',
      render: (_, record: PurchaseGroupWithTargetDTO) => `${record.supplierId}: ${record.supplierName}`,
    }),
    sortableIdColumn({
      title: 'Purchase Location',
      dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('purchaseLocationId'),
      sorter: true,
      settingKey: 'purchaseLocationId',
      render: (_, record: PurchaseGroupWithTargetDTO) => `${record.purchaseLocationId}: ${record.purchaseLocationName}`,
    }),
    ...(targetGroupType === TargetGroupType.ReviewCycle
      ? [
          sortableDateColumn({
            title: 'Last Reviewed',
            dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('lastReviewDate'),
            align: 'right' as const,
            sorter: true,
            settingKey: 'lastReviewDate',
          }),
          sortableNumberColumn({
            title: 'Review Cycle (Days)',
            dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('reviewCycleDays'),
            align: 'right' as const,
            sorter: true,
            settingKey: 'reviewCycleDays',
            render: (reviewCycleDays: number, record: PurchaseGroupWithTargetDTO) => (
              <EditTargetActionButton
                value={reviewCycleDays}
                onClick={() => editButtonOnClick(record.supplierId, record.purchaseLocationId, handleTargetValueChange)}
              />
            ),
          }),
          sortableDateColumn({
            title: 'Next Review',
            dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('nextReviewDate'),
            align: 'right' as const,
            sorter: true,
            settingKey: 'nextReviewDate',
          }),
        ]
      : [
          {
            title: 'Current',
            dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('currentValue'),
            sorter: true,
            sortDirections,
            align: 'right' as const,
            settingKey: 'currentValue',
            render: (_: unknown, record: PurchaseGroupWithTargetDTO) =>
              record.targetType === 'dollars' ? formatUSD(record.currentValue) : formatNumber(record.currentValue, 0),
          },
          {
            title: 'Target',
            dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('targetValue'),
            sorter: true,
            align: 'right' as const,
            settingKey: 'targetValue',
            render: (_: unknown, record: PurchaseGroupWithTargetDTO) => (
              <EditTargetActionButton
                value={
                  record.targetType === 'dollars' ? formatUSD(record.targetValue) : formatNumber(record.targetValue)
                }
                onClick={() => editButtonOnClick(record.supplierId, record.purchaseLocationId, handleTargetValueChange)}
              />
            ),
          },
          {
            title: 'Target Type',
            dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('targetType'),
            align: 'right' as const,
            settingKey: 'targetType',
            render: (_: unknown, record: PurchaseGroupWithTargetDTO) =>
              record.targetType ? TargetTypeLabels[record.targetType] : '-',
          },
          sortablePercentColumn({
            title: 'Target %',
            dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('goalPercent'),
            settingKey: 'goalPercent',
            render: (_, record: PurchaseGroupWithTargetDTO) => formatTargetProgressPercent(record.goalPercent),
            sorter: true,
            sortDirections,
          }),
        ]),
    {
      title: 'Status',
      dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('status'),
      settingKey: 'status',
      render: (_: unknown, record: PurchaseGroupWithTargetDTO) => <StatusBadge status={BadgeStatus[record.status]} />,
    },
    sortableDateColumn({
      title: 'Last Ordered',
      dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('lastOrderDate'),
      align: 'right',
      sorter: true,
      settingKey: 'lastOrderDate',
      render: (_, record: PurchaseGroupWithTargetDTO) => `${formatDate(record.lastOrderDate)}`,
    }),
    sortableDollarColumn({
      title: 'PO Cost',
      dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('orderCost'),
      sorter: true,
      settingKey: 'orderCost',
      render: (_, record: PurchaseGroupWithTargetDTO) => `${formatUSD(record.orderCost)}`,
    }),
    sortableNumberColumn({
      title: 'Lines',
      settingKey: 'numLines',
      dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('numLines'),
      sorter: true,
      render: (_, row) => (
        <>
          {shouldShowSpecialOrderWarning && (row.numLinesOnSpecialOrder ?? 0) > 0 && <SpecialOrderFlag />}
          {row.numLines}
        </>
      ),
    }),
    (canCreateGroups || canExportOrders) && {
      title: 'Actions',
      width: '100px',
      align: 'left' as const,
      settingKey: 'actions',
      render: (_: unknown, record: PurchaseGroupWithTargetDTO) => (
        <FlexSpace gap={5}>
          {canCreateGroups && (
            <>
              <Tooltip title="Edit">
                <Button
                  key="edit-button"
                  size="small"
                  disabled={hashState.selectedSupplierLocations && hashState.selectedSupplierLocations?.length > 0}
                  onClick={() => onPressEdit(record)}
                >
                  <EditOutlined />
                </Button>
              </Tooltip>

              <Tooltip title="Delete">
                <Button key="delete-button" size="small" onClick={() => handleDelete(record.groupId, record.groupName)}>
                  <DeleteOutlined />
                </Button>
              </Tooltip>
            </>
          )}

          {canExportOrders && (
            <Tooltip title="Lines">
              <AsyncButton
                key="open-po-button"
                size="small"
                onClick={() => onPressOpen(record.groupId)}
                spinnerPosition="absolute"
              >
                <RightOutlined />
              </AsyncButton>
            </Tooltip>
          )}
        </FlexSpace>
      ),
    },
    sortableDollarColumn({
      title: (
        <InfoTooltip
          title={`Cost of backordered ${shouldShowSpecialOrderWarning ? 'and special order ' : ''}lines`}
          useInfoIcon
        >
          Priority Cost
        </InfoTooltip>
      ),
      dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('priorityDispositionCost'),
      settingKey: 'priorityDispositionCost',
      render: (priorityDispositionCost: number, record: PurchaseGroupWithTargetDTO) => (
        <>
          {formatUSD(priorityDispositionCost)}
          <PriorityDispositionEquationPopover
            total={priorityDispositionCost}
            specialOrderQty={formatUSD(record.specialOrderCost)}
            backorderQty={formatUSD(record.backorderedCost)}
            type="Cost"
          />
        </>
      ),
      optional: true,
      sorter: true,
    }),
    sortableNumberColumn({
      title: 'Priority Lines',
      dataIndex: asKeyOf<PurchaseGroupWithTargetDTO>('numLinesPriorityDisposition'),
      settingKey: 'numLinesPriorityDisposition',
      render: (priorityLines: number, record: PurchaseGroupWithTargetDTO) => (
        <>
          {formatNumber(priorityLines)}
          <PriorityDispositionEquationPopover
            total={priorityLines}
            specialOrderQty={record.numLinesOnSpecialOrder}
            backorderQty={record.numLinesOnBackorder}
            type="Lines"
          />
        </>
      ),
      optional: true,
      sorter: true,
    }),
  ].filter(truthy);

  return { tableColumns };
}

export function usePurchaseTargets() {
  async function fetchPurchaseTargets({ locationIds, supplierIds }: { locationIds: string[]; supplierIds: string[] }) {
    const { data } = await coreApiFetch(schemas.purchasing.getPurchaseTargets, {
      queryParams: {
        filter: {
          locationIds,
          supplierIds,
        },
        offset: 0,
      },
    });

    return data.items;
  }

  function getCompanyIdFromPurchaseTargets(
    groups: (PurchaseGroupWithTargetDTO & { purchaseTargets: PurchaseTargetDTO[] })[],
  ) {
    if (
      !Array.isArray(groups) ||
      groups.length === 0 ||
      !Array.isArray(groups[0].purchaseTargets) ||
      groups[0].purchaseTargets.length === 0
    )
      return undefined;

    return groups[0].purchaseTargets[0].companyId;
  }

  async function populateGroupsWithPurchaseTargets(selectedGroups: PurchaseGroupWithTargetDTO[]) {
    const locationIds = selectedGroups.flatMap((g) => g?.spokeLocations?.map((s) => s.foreignId)).filter(truthy);
    const supplierIds = selectedGroups.map((g) => g.supplierId);

    const purchaseTargets = await fetchPurchaseTargets({ locationIds, supplierIds });

    return selectedGroups.map((group) => {
      const groupLocationIds = group.spokeLocations?.map((s) => s.foreignId) || [];
      const groupPurchaseTargets = purchaseTargets.filter(
        (pt) => groupLocationIds.includes(pt.purchaseLocationId) && group.supplierId === pt.supplierId,
      );

      return {
        ...group,
        purchaseTargets: groupPurchaseTargets,
      };
    });
  }

  return { fetchPurchaseTargets, populateGroupsWithPurchaseTargets, getCompanyIdFromPurchaseTargets };
}

export function useSuppliers() {
  async function fetchSuppliers(queryParams: GetSuppliersQueryParams) {
    const { data } = await coreApiFetch(schemas.suppliers.getSuppliers, { queryParams });

    return data.items;
  }

  async function getSupplierIdAndName(cachedSuppliers: { value: string }[], supplierId: string) {
    const supplierIdAndName = cachedSuppliers.find((s) => s.value.split(':')[0] === supplierId)?.value;
    if (supplierIdAndName) return supplierIdAndName;

    const fetchedSuppliers = await fetchSuppliers({ filter: { searchQuery: supplierId } });
    const supplier = fetchedSuppliers.find((s) => s.supplierId === supplierId);
    if (!supplier) return '';

    return `${supplier.supplierId}: ${supplier.supplierName}`;
  }

  return { fetchSuppliers, getSupplierIdAndName };
}
